import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import api from '../api';

const QuizResults = ({ quiz, attempt, onBack }) => {
    const [detailedResults, setDetailedResults] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchDetailedResults = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/quizzes/${quiz._id}/attempt/${attempt._id}/results`);
            setDetailedResults(res.data);
        } catch (error) {
            console.error('Error fetching detailed results:', error);
        } finally {
            setLoading(false);
        }
    }, [quiz._id, attempt._id]);

    useEffect(() => {
        if (quiz.showCorrectAnswers && attempt) {
            fetchDetailedResults();
        }
    }, [quiz.showCorrectAnswers, attempt, fetchDetailedResults]);

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const MathRenderer = ({ children, className = '' }) => {
        if (!children) return null;
        return (
            <div className={`prose prose-slate max-w-none ${className}`}>
                <ReactMarkdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                >
                    {children}
                </ReactMarkdown>
            </div>
        );
    };

    const getAnswerStatus = (answer, question) => {
        if (question.questionType === 'essay') {
            return { status: 'manual', color: 'bg-blue-100 text-blue-800', text: 'Chờ chấm' };
        }
        if (answer.isCorrect) {
            return { status: 'correct', color: 'bg-green-100 text-green-800', text: 'Đúng' };
        }
        return { status: 'incorrect', color: 'bg-red-100 text-red-800', text: 'Sai' };
    };

    const renderCorrectAnswer = (question) => {
        switch (question.questionType) {
            case 'multiple-choice':
                const correctOptions = question.options.filter(opt => opt.isCorrect).map(opt => opt.text);
                return correctOptions.map((text, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-1">
                        <span className="text-green-600">✓</span>
                        <MathRenderer className="text-green-700">{text}</MathRenderer>
                    </div>
                ));
            case 'true-false':
                return (
                    <span className="text-green-700">
                        {question.correctAnswer === 'true' ? 'Đúng' : 'Sai'}
                    </span>
                );
            case 'short-answer':
                return <MathRenderer className="text-green-700">{question.correctAnswer}</MathRenderer>;
            case 'essay':
                return <span className="text-slate-500">Cần chấm thủ công</span>;
            default:
                return null;
        }
    };

    const renderStudentAnswer = (answer, question) => {
        switch (question.questionType) {
            case 'multiple-choice':
                return answer.selectedOptions.length > 0 ? (
                    answer.selectedOptions.map((text, index) => (
                        <div key={index} className="flex items-center space-x-2 mb-1">
                            <span className={answer.isCorrect ? "text-green-600" : "text-red-600"}>
                                {answer.isCorrect ? "✓" : "✗"}
                            </span>
                            <MathRenderer className={answer.isCorrect ? "text-green-700" : "text-red-700"}>
                                {text}
                            </MathRenderer>
                        </div>
                    ))
                ) : (
                    <span className="text-slate-400 italic">Không có câu trả lời</span>
                );
            case 'true-false':
                return answer.selectedOptions.length > 0 ? (
                    <span className={answer.isCorrect ? "text-green-700" : "text-red-700"}>
                        {answer.selectedOptions[0] === 'true' ? 'Đúng' : 'Sai'}
                    </span>
                ) : (
                    <span className="text-slate-400 italic">Không có câu trả lời</span>
                );
            case 'short-answer':
            case 'essay':
                return answer.textAnswer.trim() ? (
                    <MathRenderer className={answer.isCorrect ? "text-green-700" : "text-red-700"}>
                        {answer.textAnswer}
                    </MathRenderer>
                ) : (
                    <span className="text-slate-400 italic">Không có câu trả lời</span>
                );
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-lg text-slate-600">Đang tải kết quả...</div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">{quiz.title}</h1>
                        <p className="text-slate-600">Kết quả bài tập</p>
                    </div>
                    <button
                        onClick={onBack}
                        className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                    >
                        ← Quay lại
                    </button>
                </div>

                {/* Score Summary */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-xl p-4">
                        <div className="text-sm text-blue-600">Điểm số</div>
                        <div className="text-2xl font-bold text-blue-700">
                            {attempt.score}/{attempt.totalPoints}
                        </div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4">
                        <div className="text-sm text-green-600">Phần trăm</div>
                        <div className="text-2xl font-bold text-green-700">
                            {attempt.percentage.toFixed(1)}%
                        </div>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4">
                        <div className="text-sm text-purple-600">Thời gian</div>
                        <div className="text-2xl font-bold text-purple-700">
                            {Math.floor(attempt.timeSpent / 60)}:{(attempt.timeSpent % 60).toString().padStart(2, '0')}
                        </div>
                    </div>
                    <div className="bg-yellow-50 rounded-xl p-4">
                        <div className="text-sm text-yellow-600">Nộp lúc</div>
                        <div className="text-sm font-medium text-yellow-700">
                            {formatDateTime(attempt.submittedAt)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Questions and Answers */}
            {detailedResults ? (
                <div className="space-y-4">
                    {detailedResults.quiz.questions.map((question, index) => {
                        const studentAnswer = detailedResults.attempt.answers[index];
                        const answerStatus = getAnswerStatus(studentAnswer, question);

                        return (
                            <div key={question._id} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                                {/* Question Header */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-sm font-semibold text-slate-500">
                                                Câu {index + 1}
                                            </span>
                                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${answerStatus.color}`}>
                                                {answerStatus.text}
                                            </span>
                                        </div>
                                        <MathRenderer className="text-lg font-medium text-slate-900">
                                            {question.questionText}
                                        </MathRenderer>
                                        {question.questionImage && (
                                            <div className="mt-3">
                                                <img
                                                    src={`http://localhost:5001${question.questionImage}`}
                                                    alt="Question"
                                                    className="max-w-full h-auto max-h-48 rounded-lg border border-slate-200 shadow-sm"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-sm text-slate-500 ml-4">
                                        {studentAnswer.pointsEarned}/{question.points} điểm
                                    </div>
                                </div>

                                {/* Answers */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Student Answer */}
                                    <div>
                                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Câu trả lời của bạn:</h4>
                                        <div className="bg-slate-50 rounded-lg p-3">
                                            {renderStudentAnswer(studentAnswer, question)}
                                        </div>
                                    </div>

                                    {/* Correct Answer */}
                                    {detailedResults.quiz.showCorrectAnswers && (
                                        <div>
                                            <h4 className="text-sm font-semibold text-slate-700 mb-2">Đáp án đúng:</h4>
                                            <div className="bg-green-50 rounded-lg p-3">
                                                {renderCorrectAnswer(question)}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Multiple Choice Options for Reference */}
                                {question.questionType === 'multiple-choice' && detailedResults.quiz.showCorrectAnswers && (
                                    <div className="mt-4 pt-4 border-t border-slate-200">
                                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Tất cả các lựa chọn:</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {question.options.map((option, optionIndex) => (
                                                <div
                                                    key={optionIndex}
                                                    className={`flex items-start space-x-2 p-2 rounded-lg ${
                                                        option.isCorrect
                                                            ? 'bg-green-50 border border-green-200'
                                                            : 'bg-slate-50 border border-slate-200'
                                                    }`}
                                                >
                                                    <span className={`text-sm mt-1 ${
                                                        option.isCorrect ? 'text-green-600' : 'text-slate-500'
                                                    }`}>
                                                        {option.isCorrect ? '✓' : '○'}
                                                    </span>
                                                    <MathRenderer className={`text-sm ${
                                                        option.isCorrect ? 'text-green-700' : 'text-slate-700'
                                                    }`}>
                                                        {option.text}
                                                    </MathRenderer>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 text-center">
                    <div className="text-slate-600">
                        {quiz.showCorrectAnswers
                            ? 'Không thể tải chi tiết kết quả.'
                            : 'Giáo viên đã tắt chức năng xem đáp án cho bài tập này.'
                        }
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuizResults;